// ===================== User settings (persisted via Save) =====================
import { Save } from './save.js';
import { CONFIG } from '../config.js';

// The OS reduced-motion preference. Created ONCE at module load and kept live
// via the `change` event — Settings.reducedMotion is read many times per frame
// (addFlash/addHitstop/camera.shake), and calling matchMedia on every read was
// measurable. Guarded so the module still imports cleanly in plain Node.
const _reducedMq = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
let _prefersReduced = !!(_reducedMq && _reducedMq.matches);
if (_reducedMq && _reducedMq.addEventListener) {
  _reducedMq.addEventListener('change', (e) => { _prefersReduced = e.matches; });
}
const prefersReduced = () => _prefersReduced;

// Device-tiered horde ceiling, decided once per boot (never mid-run — frame
// timing must not steer a sim-affecting knob, or seeded runs stop replaying).
const _isTouch = typeof window !== 'undefined'
  && (('ontouchstart' in window) || (navigator.maxTouchPoints || 0) > 0);
const _hordeCap = _isTouch ? CONFIG.spawn.maxEnemies : CONFIG.spawn.maxEnemiesDesktop;

const DEFAULTS = {
  volume: 0.6,          // 0..1 master volume
  muted: false,
  musicOff: false,      // background music disabled
  shake: true,          // screen shake
  particles: 'high',    // 'high' | 'low'
  damageNumbers: true,
  reducedMotion: null,  // null => follow OS preference
};

function rawObj() {
  if (!Save.data.settings) Save.data.settings = {};
  return Save.data.settings;
}
function get(key) {
  const v = rawObj()[key];
  return v === undefined ? DEFAULTS[key] : v;
}
function set(key, value) { rawObj()[key] = value; Save.flush(); }

export const Settings = {
  get raw() { return rawObj(); },
  stored(key) { return get(key); },
  toggle(key) { const v = !get(key); set(key, v); return v; },

  get volume() { return get('volume'); },
  set volume(v) { set('volume', Math.max(0, Math.min(1, v))); },

  get muted() { return get('muted'); },
  set muted(v) { set('muted', !!v); },

  get music() { return !get('musicOff'); },
  set music(v) { set('musicOff', !v); },

  // Effective screen shake (respects reduced-motion); UI reads stored('shake').
  get shake() { return this.reducedMotion ? false : get('shake'); },
  set shake(v) { set('shake', !!v); },

  get particles() { return get('particles'); },
  set particles(v) { set('particles', v); },
  get particleScale() { return get('particles') === 'low' ? 0.4 : 1; },

  get damageNumbers() { return get('damageNumbers'); },
  set damageNumbers(v) { set('damageNumbers', !!v); },

  get hordeCap() { return _hordeCap; },

  get reducedMotion() {
    const r = get('reducedMotion');
    return r === null || r === undefined ? prefersReduced() : r;
  },
  set reducedMotion(v) { set('reducedMotion', v); },
};
